import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { reviews } from '@/mocks/reviews';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9sm4pl8o1vn02ekuhqg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    const honeypotValue = honeypotEl?.value?.trim();

    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 4000);
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.message as string)
          || (parsed?.meta as Record<string, string>)?.detail
          || responseText
          || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }

    setTimeout(() => {
      setFormStatus('idle');
      setFormMessage('');
    }, 5000);
  };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'mrds',
      url: 'https://mrds.io',
      description: 'mrds 是您值得信赖的专业平台，提供智能数据处理、自动化工作流和实时协作等一站式解决方案。',
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-MRDS',
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Beijing',
        addressCountry: 'CN',
        streetAddress: '朝阳区科技园区',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '关于 mrds — 品牌介绍与联系方式',
      description: '了解 mrds 的品牌故事、核心团队、企业荣誉和联系方式。',
      url: 'https://mrds.io/about',
    },
  ];

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 — 品牌介绍与联系方式"
        description="了解 mrds 的品牌故事、核心团队、企业荣誉和联系方式。mrds 致力于为全球企业提供专业高效的数字化解决方案。"
        keywords="mrds 关于,mrds 品牌,mrds 联系方式,mrds 团队"
        canonicalUrl="https://mrds.io/about"
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 pb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              关于 mrds
            </h1>
            <p className="text-foreground-500 text-sm">了解 mrds 的品牌故事与核心价值</p>
          </div>
        </div>

        {/* Brand Intro */}
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-4">
                品牌故事
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5 leading-tight">
                始于对专业与效率的极致追求
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm leading-relaxed">
                <p>
                  mrds 诞生于 2020 年，由一群深耕企业服务领域多年的技术专家和产品经理共同创立。我们看到了传统企业在数字化转型过程中面临的种种挑战——数据孤岛、流程割裂、工具复杂。
                </p>
                <p>
                  基于对这些痛点的深刻理解，mrds 团队打造了一个集智能数据处理、自动化工作流、实时协作和数据可视化于一体的专业平台，致力于让每一家企业都能享受到数字化带来的效率提升。
                </p>
                <p>
                  经过多年的持续迭代和打磨，mrds 已经服务了超过 10,000 家企业客户，覆盖金融、医疗、电商、制造等多个行业，赢得了广泛的市场认可和用户信赖。
                </p>
              </div>
              <div className="flex items-center gap-8 mt-8">
                <div>
                  <div className="text-2xl font-heading font-bold text-primary-500">10,000+</div>
                  <div className="text-foreground-500 text-xs mt-1">企业客户</div>
                </div>
                <div>
                  <div className="text-2xl font-heading font-bold text-primary-500">99.9%</div>
                  <div className="text-foreground-500 text-xs mt-1">服务可用率</div>
                </div>
                <div>
                  <div className="text-2xl font-heading font-bold text-primary-500">2020</div>
                  <div className="text-foreground-500 text-xs mt-1">创立年份</div>
                </div>
              </div>
            </div>
            <div className="relative h-[400px] rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20professional%20office%20environment%2C%20diverse%20team%20collaborating%20around%20a%20table%2C%20large%20windows%20with%20natural%20light%2C%20warm%20amber%20accents%2C%20clean%20minimalist%20design%2C%20plants%20in%20office%2C%20creative%20workspace%20atmosphere&width=700&height=500&seq=about-brand&orientation=landscape"
                alt="mrds 团队"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-4">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                来自客户的真实声音
              </h2>
              <p className="text-foreground-500 text-sm max-w-[500px] mx-auto">
                mrds 的用户怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.slice(0, 3).map((review) => (
                <div key={review.id} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < review.rating ? 'ri-star-fill text-primary-400' : 'ri-star-fill text-foreground-200'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                    <img src={review.avatar} alt={review.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="text-foreground-900 text-sm font-medium">{review.name}</div>
                      <div className="text-foreground-500 text-xs">{review.role}，{review.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="max-w-[640px] mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                与我们取得联系
              </h2>
              <p className="text-foreground-500 text-sm">
                有任何问题或合作意向，欢迎通过以下表单联系我们，我们会在 24 小时内回复
              </p>
            </div>

            <form
              ref={formRef}
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8"
            >
              {formStatus === 'success' && (
                <div className="mb-6 p-4 rounded-lg bg-accent-50 border border-accent-200/50 text-accent-700 text-sm">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    <span>{formMessage}</span>
                  </div>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200/50 text-red-700 text-sm">
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    <span>{formMessage}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-foreground-700 text-sm font-medium mb-1.5">
                    姓名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-1.5">
                    邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-foreground-700 text-sm font-medium mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-foreground-700 text-sm font-medium mb-1.5">
                  留言内容 <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                  placeholder="请输入您想咨询的内容（最多500字）"
                ></textarea>
                <div className="text-foreground-400 text-xs mt-1 text-right">最多 500 字</div>
              </div>

              {/* Honeypot */}
              <div className="contact-alt-wrapper">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 text-background-50 font-medium text-sm rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '提交留言'}
              </button>
            </form>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-500 text-lg"></i>
                </div>
                <div className="text-foreground-900 text-sm font-medium mb-1">邮箱</div>
                <div className="text-foreground-500 text-xs">contact@mrds.io</div>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                  <i className="ri-phone-line text-primary-500 text-lg"></i>
                </div>
                <div className="text-foreground-900 text-sm font-medium mb-1">电话</div>
                <div className="text-foreground-500 text-xs">400-888-MRDS</div>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                  <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                </div>
                <div className="text-foreground-900 text-sm font-medium mb-1">地址</div>
                <div className="text-foreground-500 text-xs">北京市朝阳区科技园区</div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}